/**
 * threadClosureCopy — the ONE sentence a closed thread says about itself
 * (2026-09-21, owner directive).
 *
 * Chrome only, never content (D9). Four surfaces read it: the mentions tab and
 * the bell's three renderers (NotificationsBell, MobileNotifications and the
 * /notifications page).
 *
 * WHY IT IS ITS OWN MODULE rather than more keys on threadListCopy, which is
 * where the mentions tab's other chrome lives. Two reasons, both load-bearing:
 *
 *  · Three of the four surfaces are APP chrome outside the chat. Importing the
 *    thread rail's copy table into the header to say the same sentence is how
 *    one sentence quietly becomes two.
 *  · threadListCopy's parity pin invokes every function value with exactly ONE
 *    argument (tests/groupThreads/threadListCopy.test.ts), so a two-argument
 *    `archivedBy(who, when)` added there would render "… undefined …" and PASS.
 *    Here the arity is exercised for real by this module's own test.
 *
 * THE COPY BRANCHES ON THE ATTRIBUTION SHAPE, never on a boolean — see
 * threadClosure.ts. That is what makes it impossible to name a robot, to invent
 * a teammate who did nothing, or to tell a removed participant their colleagues'
 * thread was archived when it is still live for them.
 */
import type { ThreadClosure } from './threadClosure';

export type ThreadClosureLang = 'en' | 'es';

const MONTHS: Readonly<Record<ThreadClosureLang, readonly string[]>> = {
  en: ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'],
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
};

const lang = (v: ThreadClosureLang | undefined): ThreadClosureLang => (v === 'es' ? 'es' : 'en');

/**
 * The date clause, or EXACTLY '' when there is no usable stamp.
 *
 * The empty string is the whole point. `archivedAt` is written by one of the
 * four archive writers, `archivedAtTs` by another and neither by the remaining
 * two, so "no date" is the common case — and a reader that defaults to 0 and
 * formats it anyway ships "archived on jan 1 1970". Formatted from an explicit
 * month table rather than toLocaleDateString so the output is deterministic
 * under every runtime and already in the house's lowercase voice.
 */
export function threadClosureWhen(atMs: number, l?: ThreadClosureLang): string {
  if (!Number.isFinite(atMs) || atMs <= 0) return '';
  const d = new Date(atMs);
  const month = MONTHS[lang(l)][d.getMonth()];
  if (!month) return '';
  return lang(l) === 'es' ? ` el ${d.getDate()} ${month}` : ` on ${month} ${d.getDate()}`;
}

/**
 * The brief, clear line the owner asked for on hover — and the same words the
 * inline reveal and the row's accessible name carry, so the three channels can
 * never disagree about what happened.
 */
export function threadClosureSentence(closure: ThreadClosure, l?: ThreadClosureLang): string {
  const when = threadClosureWhen(closure.atMs, l);
  if (lang(l) === 'es') {
    switch (closure.attribution) {
      case 'person':
        return `${closure.byName} archivó este hilo${when} — ya no se puede abrir, pero Prest0n sigue teniendo la conversación`;
      case 'you':
        return `archivaste este hilo${when} — ya no se puede abrir`;
      case 'system':
        return `este hilo se cerró automáticamente${when} — ya no se puede abrir, pero Prest0n sigue teniendo la conversación`;
      // A removed participant, or your own archive — indistinguishable on the
      // client, so this says only what is true of both. Never "fue archivado":
      // for someone who was removed, the thread may still be live for the rest.
      case 'unknown':
      default:
        return 'este hilo ya está cerrado para ti — no se puede abrir';
    }
  }
  switch (closure.attribution) {
    case 'person':
      return `${closure.byName} archived this thread${when} — it isn't open any more, but Prest0n still has the conversation`;
    case 'you':
      return `you archived this thread${when} — it isn't open any more`;
    case 'system':
      return `this thread was closed automatically${when} — it isn't open any more, but Prest0n still has the conversation`;
    case 'unknown':
    default:
      return "this thread is closed to you now — it isn't open any more";
  }
}

/**
 * The one-word marker on the row. `archived` only where an archive is actually
 * what happened; `closed` for the no-attribution case, which covers a removal
 * the client cannot tell apart from a personal archive.
 */
export function threadClosurePill(closure: ThreadClosure, l?: ThreadClosureLang): string {
  const archived = closure.attribution !== 'unknown';
  if (lang(l) === 'es') return archived ? 'archivado' : 'cerrado';
  return archived ? 'archived' : 'closed';
}

/** The accessible name of the control that reveals the sentence. */
export function threadClosureWhyLabel(l?: ThreadClosureLang): string {
  return lang(l) === 'es' ? 'por qué está cerrado' : 'why this is closed';
}

/**
 * The retired row's accessible name, with the retired fact FIRST.
 *
 * A description (a native `title`, or `aria-describedby`) is announced after the
 * name and is routinely suppressed by verbosity settings, so the fact that the
 * row cannot be opened has to be in the NAME. `description` is whatever the row
 * would otherwise have said about itself.
 */
export function threadClosureRowName(
  closure: ThreadClosure,
  description: string,
  l?: ThreadClosureLang,
): string {
  const head = lang(l) === 'es'
    ? `${threadClosurePill(closure, l)} — no se puede abrir`
    : `${threadClosurePill(closure, l)} — can't be opened`;
  const tail = String(description || '').trim();
  return tail ? `${head}. ${tail}` : head;
}
